import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Button from '../../atoms/Button';
import PopupOverlay from '../../organisms/Background/PopupOverlay';
import Card from '../Card';
import { accountService } from '../../../services/accountService';
import './index.scss';

const BROWSER_FAMILY_KEYS = {
  chrome: 'profile.sessionsModal.browserFamilies.chrome',
  edge: 'profile.sessionsModal.browserFamilies.edge',
  firefox: 'profile.sessionsModal.browserFamilies.firefox',
  opera: 'profile.sessionsModal.browserFamilies.opera',
  safari: 'profile.sessionsModal.browserFamilies.safari',
  'samsung-browser': 'profile.sessionsModal.browserFamilies.samsungBrowser',
  ios: 'profile.sessionsModal.browserFamilies.ios',
  android: 'profile.sessionsModal.browserFamilies.android',
  other: 'profile.sessionsModal.browserFamilies.other',
  unknown: 'profile.sessionsModal.browserFamilies.unknown',
};

const MOBILE_USER_AGENT_PATTERN = /android|iphone|ipad|ipod|mobile/i;

const formatDateTime = (value, locale) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(locale || undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message
  || error?.body?.message
  || fallback
);

const ActiveSessionsModal = ({ onClose, onCurrentSessionTerminated = undefined }) => {
  const { t, i18n } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [terminatingId, setTerminatingId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const locale = i18n?.language || undefined;

  const loadSessions = useCallback(async ({ silent = false, isMounted = () => true } = {}) => {
    if (!isMounted()) {
      return;
    }
    if (silent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setFeedback(null);

    try {
      const data = await accountService.listActiveSessions();
      if (isMounted()) {
        setSessions(data);
      }
    } catch (error) {
      if (isMounted()) {
        setFeedback({
          type: 'error',
          message: getErrorMessage(error, t('profile.sessionsModal.errors.load')),
        });
      }
    } finally {
      if (isMounted()) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [t]);

  useEffect(() => {
    let mounted = true;
    loadSessions({ isMounted: () => mounted });
    return () => {
      mounted = false;
    };
  }, [loadSessions]);

  const sortedSessions = useMemo(() => [...sessions].sort((a, b) => {
    if (a?.current && !b?.current) {
      return -1;
    }
    if (!a?.current && b?.current) {
      return 1;
    }
    return new Date(b?.lastUsedAt || 0).getTime() - new Date(a?.lastUsedAt || 0).getTime();
  }), [sessions]);

  const describeSession = (session) => {
    const familyKey = BROWSER_FAMILY_KEYS[session?.userAgentFamily] || BROWSER_FAMILY_KEYS.unknown;
    const browser = t(familyKey);
    const device = MOBILE_USER_AGENT_PATTERN.test(session?.userAgent || '')
      ? t('profile.sessionsModal.deviceTypes.mobile')
      : t('profile.sessionsModal.deviceTypes.desktop');
    return `${browser} - ${device}`;
  };

  const handleTerminate = async (sessionId) => {
    if (!sessionId || terminatingId) {
      return;
    }

    setTerminatingId(sessionId);
    setFeedback(null);
    try {
      const result = await accountService.terminateSession(sessionId);
      if (result?.currentSessionTerminated === true) {
        onCurrentSessionTerminated?.();
        onClose();
        return;
      }
      setSessions((prev) => prev.filter((session) => session.id !== sessionId));
      setFeedback({ type: 'success', message: t('profile.sessionsModal.feedback.terminated') });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getErrorMessage(error, t('profile.sessionsModal.errors.terminate')),
      });
    } finally {
      setTerminatingId(null);
    }
  };

  const isBusy = isLoading || isRefreshing || terminatingId !== null;
  const canDismiss = !isRefreshing && terminatingId === null;
  const portalTarget = typeof document !== 'undefined'
    ? document.getElementById('portal')
    : null;

  const overlay = (
    <PopupOverlay
      onClose={onClose}
      closeOnEsc={canDismiss}
      closeOnOverlayClick={canDismiss}
      aria-label={t('profile.sessionsModal.title')}
    >
      <Card
        className="account-security-card active-sessions-card"
        title={(
          <strong className="account-security-card__title active-sessions-card__title">
            {t('profile.sessionsModal.title')}
          </strong>
        )}
        body={(
          <div className="account-security-card__content active-sessions-card__content" aria-busy={isLoading}>
            {feedback && (
              <div
                className={`active-sessions-card__feedback active-sessions-card__feedback--${feedback.type}`}
                role={feedback.type === 'error' ? 'alert' : 'status'}
              >
                {feedback.message}
              </div>
            )}

            {isLoading ? (
              <div className="active-sessions-card__state" role="status" aria-live="polite">
                {t('profile.sessionsModal.loading')}
              </div>
            ) : sortedSessions.length === 0 ? (
              <div className="active-sessions-card__state">
                {t('profile.sessionsModal.empty')}
              </div>
            ) : (
              <ul className="active-sessions-card__list" aria-label={t('profile.sessionsModal.listLabel')}>
                {sortedSessions.map((session) => {
                  const sessionId = session.id;
                  const isTerminating = terminatingId === sessionId;
                  const lastUsedAt = formatDateTime(session.lastUsedAt, locale);
                  const createdAt = formatDateTime(session.createdAt, locale);
                  const expiresAt = formatDateTime(session.expiresAt, locale);

                  return (
                    <li className="active-sessions-card__session" key={sessionId}>
                      <div className="active-sessions-card__session-main">
                        <div className="active-sessions-card__session-heading">
                          <span className="active-sessions-card__session-title" title={session.userAgent || undefined}>
                            {describeSession(session)}
                          </span>
                          {session.current && (
                            <span className="active-sessions-card__current">
                              {t('profile.sessionsModal.current')}
                            </span>
                          )}
                        </div>
                        <dl className="active-sessions-card__details">
                          <div>
                            <dt>{t('profile.sessionsModal.fields.lastActive')}</dt>
                            <dd>{lastUsedAt || t('profile.sessionsModal.unknown')}</dd>
                          </div>
                          <div>
                            <dt>{t('profile.sessionsModal.fields.started')}</dt>
                            <dd>{createdAt || t('profile.sessionsModal.unknown')}</dd>
                          </div>
                          <div>
                            <dt>{t('profile.sessionsModal.fields.expires')}</dt>
                            <dd>{expiresAt || t('profile.sessionsModal.unknown')}</dd>
                          </div>
                          <div>
                            <dt>{t('profile.sessionsModal.fields.ip')}</dt>
                            <dd>{session.ipLast || t('profile.sessionsModal.unknown')}</dd>
                          </div>
                        </dl>
                      </div>
                      <Button
                        variant="secondary"
                        shape="pill"
                        size="small"
                        className="active-sessions-card__terminate"
                        onClick={() => handleTerminate(sessionId)}
                        disabled={isBusy}
                      >
                        {isTerminating
                          ? t('profile.sessionsModal.actions.terminating')
                          : t(session.current
                            ? 'profile.sessionsModal.actions.terminateCurrent'
                            : 'profile.sessionsModal.actions.terminate')}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        footer={(
          <div className="account-security-card__actions active-sessions-card__actions">
            <Button
              variant="secondary"
              shape="pill"
              className="account-security-card__secondary active-sessions-card__secondary"
              onClick={onClose}
              disabled={isBusy && terminatingId !== null}
            >
              {t('profile.sessionsModal.actions.close')}
            </Button>
            <Button
              variant="primary"
              shape="pill"
              className="account-security-card__primary active-sessions-card__primary"
              onClick={() => loadSessions({ silent: true })}
              disabled={isBusy}
            >
              {isRefreshing
                ? t('profile.sessionsModal.actions.refreshing')
                : t('profile.sessionsModal.actions.refresh')}
            </Button>
          </div>
        )}
      />
    </PopupOverlay>
  );

  return portalTarget ? createPortal(overlay, portalTarget) : overlay;
};

ActiveSessionsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCurrentSessionTerminated: PropTypes.func,
};

export default ActiveSessionsModal;
